import React, { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, IconButton, Tooltip } from '@mui/material';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import { Videocam } from '@mui/icons-material';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';

import CloseIcon from '@mui/icons-material/Close';

import { useConference } from '../../../../DashboardContent/Dolby';
import { sendCustomSentryBreadcrumb } from '../../../../../../../helpers/sentry';
import { getCatchErrorMessage } from '../../../../../../../helpers/error';
import { useMytaverseEvent } from '../../../../../providers';

import { IProps } from './interfaces';
import { useStyles } from './styles';
import { useZoom } from '../../../../DashboardContent/Zoom/ZoomProvider';
import { AnalyticsType } from '../../../../../../../interfaces/analytics';

const DolbyShareWebcamButton = ({
  cameraDeviceId,
  onVideoClick,
  iconColorToggle,
  showWarningBadge,
}: IProps) => {
  const [open, setOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const { t: translate } = useTranslation('common');

  const {
    ueWebcamScreenName,
    ueWebcamMediaStreams,
    isConferenceListener,
    videoStarted,
  } = useConference();

  const {
    startShareCameraToScreen,
    stopShareCameraToScreen,
    zoomUser,
    mediaStream,
    zmClient,
    isCameraOn,
  } = useZoom();

  const activeCamera = mediaStream?.getActiveCamera();

  const { currentParticipant, canSpeak, trackEventAnalytics } = useMytaverseEvent();

  const currentParticipantId = currentParticipant?.participantId || '';

  const handleSharingButtonClick = React.useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.currentTarget.blur();

      if (cameraDeviceId) {
        setOpen((prevState) => !prevState);
      }
    },
    [cameraDeviceId],
  );

  const currentCameraSharing = React.useMemo(() => {
    return !!ueWebcamMediaStreams.find(
      (ms) =>
        ms.participantId === currentParticipantId &&
        ms.ueScreenName === ueWebcamScreenName &&
        ms.source === 'CAMERA',
    );
  }, [ueWebcamMediaStreams, ueWebcamScreenName, currentParticipantId]);

  const classes = useStyles({
    showPulseAnimation:
      isCameraOn || ueWebcamMediaStreams.length === 0 || !currentCameraSharing,
  });

  const handleDolbyShareWebcamClick = React.useCallback(async () => {
    try {
      const user = zmClient.getCurrentUserInfo();
      if (ueWebcamScreenName) {
        console.log({ user });
        if (user?.bVideoOn && ueWebcamMediaStreams.length !== 0) {
          sendCustomSentryBreadcrumb({
            message: 'Stop zoom share webcam to UE',
          });
          console.log('Stop zoom share webcam to UE');
          await stopShareCameraToScreen();
          trackEventAnalytics(AnalyticsType.CameraShareFinish);
        } else {
          sendCustomSentryBreadcrumb({
            message: 'Start zoom share webcam to UE',
          });
          console.log('Start zoom share webcam to UE');
          await startShareCameraToScreen();
          trackEventAnalytics(AnalyticsType.CameraShareStart);
        }
      }
    } catch (error: unknown) {
      if (canSpeak) {
        //showNotification(getDolbyNotification());
      }
      throw Error(getCatchErrorMessage(error));
    }
  }, [
    ueWebcamScreenName,
    ueWebcamMediaStreams,
    zoomUser,
    currentCameraSharing,
    canSpeak,
    //activeCamera,
    zmClient,
    trackEventAnalytics
  ]);

  return (
    <Box sx={classes.root}>
      <Box sx={classes.container(open)}>
        <Tooltip
          title={translate(
            ueWebcamMediaStreams.length !== 0 && currentCameraSharing
              ? 'tooltips.stopShareCam'
              : 'tooltips.shareCam',
          )}
          placement="top"
        >
          <IconButton
            sx={classes.button}
            onClick={handleDolbyShareWebcamClick}
            disabled={
              ueWebcamMediaStreams.length !== 0 && !currentCameraSharing
            }
            disableRipple
          >
            <VideoCameraFrontIcon
              sx={iconColorToggle(
                ueWebcamMediaStreams.length !== 0 && currentCameraSharing,
              )}
            />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={translate(
            isCameraOn && !!cameraDeviceId && !isConferenceListener
              ? 'tooltips.turnOffCam'
              : 'tooltips.turnOnCam',
          )}
        >
          <IconButton
            sx={classes.button}
            onClick={onVideoClick}
            disabled={!cameraDeviceId || currentCameraSharing}
            disableRipple
          >
            <VideocamOffIcon
              sx={iconColorToggle(
                isCameraOn && !!cameraDeviceId && !isConferenceListener,
              )}
            />
          </IconButton>
        </Tooltip>
        <Divider sx={classes.deliver} />
      </Box>

      <Tooltip
        title={translate('tooltips.toggleCam')}
        open={showTooltip && !open}
      >
        <Box sx={classes.buttonWrapper}>
          {(isCameraOn ||
            ueWebcamMediaStreams.length === 0 ||
            !currentCameraSharing) && <Box sx={classes.warning} />}
          <IconButton
            onClick={handleSharingButtonClick}
            disableRipple
            sx={classes.mainButton}
            onMouseEnter={() => {
              setShowTooltip(true);
            }}
            onMouseLeave={() => {
              setShowTooltip(false);
            }}
          >
            {showWarningBadge(
              open ? (
                <CloseIcon sx={classes.close} />
              ) : videoStarted && !!activeCamera && !isConferenceListener ? (
                <Videocam
                  sx={iconColorToggle(
                    videoStarted && !!activeCamera && !isConferenceListener,
                  )}
                />
              ) : (
                <VideocamOffIcon
                  sx={iconColorToggle(
                    videoStarted && !!activeCamera && !isConferenceListener,
                  )}
                />
              ),
            )}
          </IconButton>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default DolbyShareWebcamButton;
